import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ClientConfigPanel } from '../components/ClientConfigPanel.js';
import {
  ApiError,
  isVlessClient,
  me as meApi,
  type SerializedClientConfig,
  type SerializedConfig,
} from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

/**
 * Config detail screen — also where the just-minted private key is shown
 * exactly once. After the user has had a chance to download / scan the QR,
 * the cached client payload is wiped from sessionStorage so a refresh of
 * the page no longer leaks the private key.
 */
export function ConfigDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const navigate = useNavigate();
  const auth = useAuth();

  const [config, setConfig] = useState<SerializedConfig | null>(null);
  const [client, setClient] = useState<SerializedClientConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  // Pull the optional cached client config (set by Dashboard's create/reissue
  // flow). Read once on mount; the eviction happens after the user
  // acknowledges that they've saved the config locally.
  useEffect(() => {
    const cached = sessionStorage.getItem(`vpn.client.${id}`);
    if (cached) {
      try {
        setClient(JSON.parse(cached) as SerializedClientConfig);
      } catch {
        // Ignore — corrupted payload, just fall back to the saved-config view.
      }
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await meApi.listConfigs();
        if (cancelled) return;
        const hit = list.items.find((c) => c.id === id);
        if (!hit) {
          setError('Конфиг не найден.');
          return;
        }
        setConfig(hit);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          auth.logout();
          navigate('/login', { replace: true });
          return;
        }
        setError(err instanceof Error ? err.message : 'Не удалось загрузить конфиг.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, auth, navigate]);

  const onAcknowledge = () => {
    sessionStorage.removeItem(`vpn.client.${id}`);
    setClient(null);
    setAcknowledged(true);
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <div className="mb-6 flex items-center justify-between">
        <Link to="/" className="text-sm text-slate-400 hover:text-white">
          ← К списку конфигов
        </Link>
        {config && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              config.status === 'REVOKED'
                ? 'bg-slate-800 text-slate-400'
                : 'bg-emerald-500/15 text-emerald-300'
            }`}
          >
            {config.status === 'REVOKED' ? 'отозван' : 'активен'}
          </span>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {config && (
        <header>
          <h1 className="text-2xl font-semibold">{config.name}</h1>
          <p className="mt-1 font-mono text-xs text-slate-500">
            {/* VLESS assigns no tunnel address, so the protocol takes its slot. */}
            {config.clientIp ?? 'VLESS'} · {config.publicKey}
          </p>
        </header>
      )}

      {client && !acknowledged ? (
        <FreshlyMintedBlock
          client={client}
          configName={config?.name ?? 'config'}
          onAcknowledge={onAcknowledge}
        />
      ) : config ? (
        <SavedConfigBlock acknowledged={acknowledged} />
      ) : null}
    </div>
  );
}

function FreshlyMintedBlock({
  client,
  configName,
  onAcknowledge,
}: {
  client: SerializedClientConfig;
  configName: string;
  onAcknowledge: () => void;
}) {
  return (
    <section className="mt-6 rounded-xl border border-amber-500/40 bg-amber-500/5 p-5">
      <h2 className="text-sm font-semibold text-amber-200">Сохраните конфиг прямо сейчас</h2>
      <p className="mt-1 text-xs text-slate-300">
        Приватный ключ показывается ровно один раз — после подтверждения он будет удалён. Если
        потеряете его, вам придётся перевыпустить конфиг.
      </p>
      {!isVlessClient(client) && client.vpnUrl != null && (
        <p className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-200">
          <b>Совет.</b> Берите формат <b>vpn://</b> — приложение AmneziaVPN импортирует конфиг в один
          тап. <b>.conf</b> нужен только для нативного AmneziaWG-клиента или роутера.
        </p>
      )}

      <div className="mt-4">
        <ClientConfigPanel client={client} configName={configName} />
      </div>

      <button
        onClick={onAcknowledge}
        className="mt-6 w-full rounded-lg border border-amber-500/40 bg-amber-500/10 py-2 text-sm font-medium text-amber-100 hover:bg-amber-500/20"
      >
        Я сохранил конфиг — стереть приватный ключ
      </button>
    </section>
  );
}

function SavedConfigBlock({ acknowledged }: { acknowledged: boolean }) {
  return (
    <section className="mt-6 rounded-xl border border-slate-800 bg-slate-900/50 p-5">
      <h2 className="text-sm font-semibold text-slate-200">Сохранённый конфиг</h2>
      <p className="mt-2 text-xs text-slate-400">
        Приватный ключ конфига уже не хранится в кабинете. Если ключ утерян, перевыпустите конфиг
        в дашборде — старый сразу перестанет работать.
      </p>
      {acknowledged && (
        <p className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-200">
          Приватный ключ удалён из кабинета.
        </p>
      )}
    </section>
  );
}
